// ============================================================
// useVideoEditor.js — editor state machine for the Video Editor panel (v1)
// ------------------------------------------------------------
// Scope v1:
//   • Source clip selection (mock library for now)
//   • Timeline trim (in / out, in seconds)
//   • Timed text overlays (text + in/out timestamps + position/style)
//   • Subtitles — Whisper transcription inserted as ordinary text overlays
//   • Music selection (mock catalogue; Jamendo later)
//   • Persistence to public.video_projects via /api/video/projects
//   • Render submission to the backend stub (/api/video/render)
//
// Phase 7: persistence moved off the browser Supabase client. `clientId` here
// is the SIGNED-IN user, so saving a project while viewing as a client wrote
// the row — and its first clip — under the ADMIN's id. Scope is server-side
// now. The prop is still the "workspace loaded yet" guard it always was, and
// the useCallback deps that name it are unchanged and correct.
//
// Persistence + render-status fields map 1:1 to the migration so the render
// provider (Rendi) is a drop-in swap behind the existing backend route.
// ============================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clientScopedFetch } from '../lib/authedFetch';
import { resolvePresetSettings, buildCSSFilterString } from '../utils/filterEngine';
import { resolveFont, DEFAULT_FONT_ID } from '../utils/videoFonts';

const BACKEND =
  process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';

// Cap on hand-placed text overlays. Subtitle overlays are exempt and are not
// counted against it: a real transcript routinely runs to dozens of segments,
// and capping it would silently drop the tail of what was said. Tagging each
// overlay with its source keeps "+ Add text overlay" usable after subtitles
// have been inserted, instead of it being permanently blocked by the total.
export const MAX_TEXT_OVERLAYS = 8;

const uid = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `id_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);

// Overlays created before `source` existed are hand-placed by definition.
const isSubtitle = (o) => o?.source === 'subtitle';
const manualCount = (list) => list.filter((o) => !isSubtitle(o)).length;

function newOverlay(at = 0, clipEnd = 5, overrides = {}) {
  const inTime = Math.max(0, at);
  return {
    id: uid(),
    text: 'New text',
    inTime,
    outTime: Math.min(clipEnd, inTime + 3),
    x: 50, // percent of frame width  (center)
    y: 80, // percent of frame height (lower third)
    size: 36, // px at 1080-tall reference
    color: '#FFFFFF',
    align: 'center',
    font: DEFAULT_FONT_ID,   // font id from videoFonts (renderer downloads the .ttf)
    animationType: 'none',   // none|fadeIn|fadeOut|fadeBoth|slideUp|slideDown
    source: 'manual',        // 'manual' | 'subtitle' — only 'manual' counts against the cap
    ...overrides,
  };
}

export function useVideoEditor({ clientId, showToast }) {
  const [projectId, setProjectId]   = useState(null);
  const [title, setTitle]           = useState('Untitled Video');
  const [clip, setClip]             = useState(null); // primary source clip (mock for v1)
  const [duration, setDuration]     = useState(0);    // measured clip duration (s)
  const [trimStart, setTrimStart]   = useState(0);
  const [trimEnd, setTrimEnd]       = useState(0);
  const [overlays, setOverlays]     = useState([]);
  const [activeOverlayId, setActiveOverlayId] = useState(null);
  const [music, setMusic]           = useState(null); // { trackId, title, artist, volume, startAt }
  const [filterPreset, setFilterPreset] = useState('Normal'); // photo-editor preset name
  const [isSaving, setIsSaving]     = useState(false);
  const [subtitlesLoading, setSubtitlesLoading] = useState(false); // transcription in flight
  const [renderStatus, setRenderStatus] = useState('draft'); // draft|processing|completed|failed
  const [renderJobId, setRenderJobId]   = useState(null);
  const [outputUrl, setOutputUrl]       = useState(null);    // URL of the finished render
  const [errorMessage, setErrorMessage] = useState(null);    // reason a render failed
  const [dirty, setDirty]               = useState(false);   // unsaved edits since last save/open
  const pollRef = useRef(null);                              // setInterval handle for status polling

  const markDirty = useCallback(() => setDirty(true), []);

  const toast = useCallback((m) => { if (showToast) showToast(m); }, [showToast]);

  // ── Render status polling ───────────────────────────────────────────────────
  // After a render is queued we poll the backend every 5s until it resolves to
  // 'completed' (with an output URL) or 'failed'. Transient fetch errors are
  // swallowed so a network blip doesn't kill an in-flight render.
  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback((jobId) => {
    if (!jobId) return;
    stopPolling(); // never run two pollers at once
    pollRef.current = setInterval(async () => {
      try {
        const res = await clientScopedFetch(`${BACKEND}/api/video/render/${jobId}`);
        const json = await res.json().catch(() => ({}));
        if (!res.ok) return; // transient server error — keep polling
        if (json.status === 'completed') {
          stopPolling();
          setOutputUrl(json.outputUrl || null);
          setRenderStatus('completed');
          toast('Render complete ✓');
        } else if (json.status === 'failed') {
          stopPolling();
          setErrorMessage(json.error || 'Render failed');
          setRenderStatus('failed');
          toast(`Render failed: ${json.error || 'unknown error'}`);
        }
        // status 'processing' → keep polling
      } catch (err) {
        console.warn('[VideoEditor] poll error:', err.message); // network blip — keep polling
      }
    }, 5000);
  }, [stopPolling, toast]);

  // Stop any in-flight poller when the editor unmounts.
  useEffect(() => () => stopPolling(), [stopPolling]);

  // Unsaved-changes flag: true once the user mutates a loaded/created project,
  // reset to false whenever we open, create, clear, or save a project.
  const isDirty = !!clip && dirty;

  // Title edits count as changes too (wraps the raw setter for the UI).
  const updateTitle = useCallback((v) => { setTitle(v); markDirty(); }, [markDirty]);

  // ── Clip selection ────────────────────────────────────────────────────────
  const loadClip = useCallback((c) => {
    stopPolling();
    setClip(c);
    const d = c?.duration || 0;
    setDuration(d);
    setTrimStart(0);
    setTrimEnd(d);
    setOverlays([]);
    setActiveOverlayId(null);
    setMusic(null);
    setFilterPreset('Normal');
    setProjectId(null);
    setRenderStatus('draft');
    setRenderJobId(null);
    setOutputUrl(null);
    setErrorMessage(null);
    setTitle(c?.label ? `${c.label} Edit` : 'Untitled Video');
    setDirty(false);
  }, [stopPolling]);

  const clearProject = useCallback(() => {
    stopPolling();
    setClip(null);
    setDuration(0);
    setTrimStart(0);
    setTrimEnd(0);
    setOverlays([]);
    setActiveOverlayId(null);
    setMusic(null);
    setFilterPreset('Normal');
    setProjectId(null);
    setRenderStatus('draft');
    setRenderJobId(null);
    setOutputUrl(null);
    setErrorMessage(null);
    setTitle('Untitled Video');
    setDirty(false);
  }, [stopPolling]);

  // ── Open a saved project ────────────────────────────────────────────────────
  // Restores the full editor state from a video_projects row. edit_state holds
  // the clip + overlays + music + filter (written by editState() on save); the
  // top-level columns carry the title, trim window, and render lifecycle. Older
  // overlays are upgraded with defaults for fields added later (font/animation).
  const loadProject = useCallback((row) => {
    if (!row) return;
    stopPolling();
    const es = row.edit_state || {};
    const c  = es.clip || null;
    const d  = (c && c.duration) || 0;

    setProjectId(row.id);
    setTitle(row.title || 'Untitled Video');
    setClip(c);
    setDuration(d);
    setTrimStart(Number(row.trim_start) || 0);
    setTrimEnd(row.trim_end != null ? Number(row.trim_end) : d);
    setOverlays(
      (Array.isArray(es.textOverlays) ? es.textOverlays : []).map((o) => ({
        font: DEFAULT_FONT_ID,
        animationType: 'none',
        source: 'manual', // overlays saved before subtitles existed are hand-placed
        ...o,
      })),
    );
    setActiveOverlayId(null);
    setMusic(es.music || null);
    setFilterPreset(es.filterPreset || 'Normal');
    setRenderStatus(row.render_status || 'draft');
    setRenderJobId(row.render_job_id || null);
    setOutputUrl(row.output_url || null);
    setErrorMessage(row.error_message || null);
    setDirty(false);

    // Resume polling if we reopened a project mid-render.
    if (row.render_status === 'processing' && row.render_job_id) {
      startPolling(row.render_job_id);
    }
  }, [stopPolling, startPolling]);

  // Called when the <video> element reports its real duration.
  const setMeasuredDuration = useCallback((d) => {
    if (!d || Number.isNaN(d)) return;
    setDuration((prev) => {
      if (prev) return prev;       // keep mock duration if already set
      return d;
    });
    setTrimEnd((prev) => (prev > 0 ? prev : d));
  }, []);

  // ── Timeline trim ───────────────────────────────────────────────────────────
  const updateTrim = useCallback((next) => {
    markDirty();
    setTrimStart((prevStart) => {
      let start = next.start != null ? next.start : prevStart;
      start = Math.max(0, start);
      return start;
    });
    setTrimEnd((prevEnd) => {
      let end = next.end != null ? next.end : prevEnd;
      if (duration) end = Math.min(duration, end);
      return end;
    });
  }, [duration, markDirty]);

  // ── Text overlays ────────────────────────────────────────────────────────────
  const addOverlay = useCallback((at) => {
    setOverlays((prev) => {
      if (manualCount(prev) >= MAX_TEXT_OVERLAYS) {
        toast(`Max ${MAX_TEXT_OVERLAYS} text overlays`);
        return prev;
      }
      const o = newOverlay(at != null ? at : trimStart, trimEnd || duration || 5);
      setActiveOverlayId(o.id);
      markDirty();
      return [...prev, o];
    });
  }, [trimStart, trimEnd, duration, toast, markDirty]);

  const updateOverlay = useCallback((id, changes) => {
    markDirty();
    setOverlays((prev) => prev.map((o) => (o.id === id ? { ...o, ...changes } : o)));
  }, [markDirty]);

  const removeOverlay = useCallback((id) => {
    markDirty();
    setOverlays((prev) => prev.filter((o) => o.id !== id));
    setActiveOverlayId((cur) => (cur === id ? null : cur));
  }, [markDirty]);

  const selectOverlay = useCallback((id) => setActiveOverlayId(id), []);

  // Clearing the selection matters for more than tidiness: the canvas pins the
  // active overlay visible regardless of the playhead, so one left selected
  // covers whatever should actually be on screen and intercepts its pointer
  // events. Stable identity — VideoCanvas keys a keydown effect off it.
  const deselectOverlay = useCallback(() => setActiveOverlayId(null), []);

  // Clicking the already-selected layer row toggles it back off.
  const toggleOverlay = useCallback(
    (id) => setActiveOverlayId((cur) => (cur === id ? null : id)),
    [],
  );

  // ── Subtitles ───────────────────────────────────────────────────────────────
  // Manually triggered from the Text tab. The backend strips the clip's audio
  // and runs it through Whisper, and we turn each returned segment into a
  // regular overlay — same shape, same updateOverlay/drag/timing controls, same
  // drawtext burn-in at render. There is no separate subtitle asset anywhere.
  //
  // Every segment lands in ONE setOverlays call so React commits a single
  // update, and re-running replaces the previous transcript rather than
  // stacking a second copy on top of it. Hand-placed overlays are preserved.
  const addSubtitles = useCallback(async () => {
    if (!clip?.url) { toast('Pick a clip first'); return null; }
    if (subtitlesLoading) return null;
    setSubtitlesLoading(true);
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/video/transcribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clipUrl:   clip.url,
          trimStart: Number(trimStart) || 0,
          trimEnd:   Number(trimEnd) || duration || null,
        }),
      });
      const json = await res.json().catch(() => ({}));
      // The backend returns a human-readable reason for every failure it knows
      // about (no audio track, no speech, too long, not configured) — surface it
      // as-is rather than collapsing them into one generic message.
      if (!res.ok) throw new Error(json.error || `Transcription failed (${res.status})`);

      const segments = Array.isArray(json.segments) ? json.segments : [];
      if (segments.length === 0) throw new Error('No speech was detected in this video.');

      // Whisper timestamps are on the source clip's timeline, which is the same
      // timeline overlay in/out times use — no rebasing. Clamp the tail segment
      // to the trim end so the layer list never shows a time past the cut.
      const clipEnd = trimEnd || duration || 0;
      const made = segments
        .map((s) => {
          const inTime  = Math.max(0, Number(s.start) || 0);
          const rawOut  = Number(s.end) || 0;
          const outTime = clipEnd ? Math.min(clipEnd, rawOut) : rawOut;
          return outTime > inTime
            ? newOverlay(inTime, clipEnd, { text: s.text, outTime, source: 'subtitle' })
            : null;
        })
        .filter(Boolean);
      if (made.length === 0) throw new Error('No speech was detected in this video.');

      setOverlays((prev) => [...prev.filter((o) => !isSubtitle(o)), ...made]);
      setActiveOverlayId(null);
      markDirty();
      toast(
        json.truncated
          ? `Added the first ${made.length} subtitles — this video was too long to caption in full`
          : `Added ${made.length} subtitle${made.length !== 1 ? 's' : ''} ✓`,
      );
      return made;
    } catch (err) {
      console.error('[VideoEditor] subtitles failed:', err);
      toast(err.message);
      return null;
    } finally {
      setSubtitlesLoading(false);
    }
  }, [clip, trimStart, trimEnd, duration, subtitlesLoading, toast, markDirty]);

  // ── Music ─────────────────────────────────────────────────────────────────
  const selectTrack = useCallback((track) => {
    markDirty();
    if (!track) { setMusic(null); return; }
    setMusic({
      trackId: track.id,
      title:   track.title,
      artist:  track.artist,
      url:     track.url || null,
      volume:  music?.volume ?? 70,
      startAt: 0,
    });
  }, [music, markDirty]);

  const updateMusic = useCallback((changes) => {
    markDirty();
    setMusic((prev) => (prev ? { ...prev, ...changes } : prev));
  }, [markDirty]);

  // ── Filter (photo-editor presets) ───────────────────────────────────────────
  const selectFilter = useCallback((presetName) => {
    markDirty();
    setFilterPreset(presetName || 'Normal');
  }, [markDirty]);

  // CSS `filter` string for the live preview (canvas-safe primitives, applied to
  // the <video> element only so text overlays stay unfiltered).
  const filterCss = useMemo(
    () => buildCSSFilterString(resolvePresetSettings(filterPreset, 1)),
    [filterPreset],
  );

  // ── Render instruction payload (drop-in shape for the provider) ─────────────
  const buildRenderPayload = useCallback(() => ({
    projectId: projectId || null,
    // clientId REMOVED (Phase 7). requireOwnerOrAdmin reads a body-borne client
    // id BEFORE ?client=, so leaving this here would have let the signed-in
    // admin's id beat the viewing-as override on every render — silently.
    title,
    source: {
      clips: clip ? [{ id: clip.id, url: clip.url, duration: clip.duration }] : [],
    },
    timeline: {
      trimStart: Number(trimStart) || 0,
      trimEnd:   Number(trimEnd) || duration || 0,
    },
    textOverlays: overlays.map((o) => {
      const f = resolveFont(o.font);
      return {
        id: o.id,
        text: o.text,
        inTime: o.inTime,
        outTime: o.outTime,
        position: { x: o.x, y: o.y },
        style: { size: o.size, color: o.color, align: o.align },
        // Font ships the .ttf URL so Rendi can fetch it as a drawtext fontfile.
        font: { id: f.id, url: f.ttfUrl || null },
        animationType: o.animationType || 'none',
      };
    }),
    music: music
      ? { trackId: music.trackId, title: music.title, url: music.url, volume: music.volume, startAt: music.startAt }
      : null,
    // Colour filter for Rendi to apply via FFmpeg (null = no filter). We pass the
    // preset name + resolved adjustment settings + the canvas-safe CSS string.
    filter: filterPreset && filterPreset !== 'Normal'
      ? {
          preset:   filterPreset,
          settings: resolvePresetSettings(filterPreset, 1),
          css:      filterCss,
        }
      : null,
    output: { format: 'mp4', resolution: '1080x1920', fps: 30 },
  }), [projectId, title, clip, trimStart, trimEnd, duration, overlays, music, filterPreset, filterCss]);

  const editState = useCallback(() => ({
    textOverlays: overlays,
    music,
    filterPreset,
    clip: clip ? { id: clip.id, url: clip.url, filename: clip.filename, duration: clip.duration, aspect: clip.aspect } : null,
  }), [overlays, music, filterPreset, clip]);

  // ── Persistence ─────────────────────────────────────────────────────────────
  //
  // Three call sites collapsed into one request. The update branch, the insert
  // branch, and the first-clip insert that runs only when the project is new
  // and depends on the id the insert returns — a sequence the browser could not
  // have scoped anyway, since the update filtered on id alone.
  //
  // The clip insert stays non-fatal server-side, exactly as it was here: the
  // project is saved by then, and failing the whole save over a bookkeeping row
  // would lose the user's edit.
  const saveProject = useCallback(async () => {
    if (!clientId) { toast('Still loading your workspace — try again'); return null; }
    if (!clip)     { toast('Pick a clip first'); return null; }
    setIsSaving(true);
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/video/projects`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          projectId:  projectId || null,
          title,
          trim_start: Number(trimStart) || 0,
          trim_end:   trimEnd != null ? Number(trimEnd) : null,
          edit_state: editState(),
          // Sent every time; the route inserts it only when the project is new.
          clip: {
            url:      clip.url,
            filename: clip.filename || null,
            duration: clip.duration || null,
          },
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setProjectId(body.projectId);
      setDirty(false);
      toast('Project saved ✓');
      return body.projectId;
    } catch (err) {
      console.error('[VideoEditor] save failed:', err);
      toast(`Save failed: ${err.message}`);
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [clientId, clip, title, trimStart, trimEnd, projectId, editState, toast]);

  // ── Render (backend stub → Rendi later) ─────────────────────────────────────
  const submitRender = useCallback(async () => {
    if (!clientId) { toast('Still loading your workspace — try again'); return null; }
    if (!clip)     { toast('Pick a clip first'); return null; }
    setIsSaving(true);
    try {
      // Make sure the project is persisted so the backend has a row to update.
      let pid = projectId;
      if (!pid) pid = await saveProject();
      if (!pid) return null;

      const payload = { ...buildRenderPayload(), projectId: pid };
      const res = await clientScopedFetch(`${BACKEND}/api/video/render`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `Render request failed (${res.status})`);

      const jobId = json.jobId || null;
      const status = json.status || 'processing';
      setRenderStatus(status);
      setRenderJobId(jobId);
      setOutputUrl(null);
      setErrorMessage(null);
      if (jobId && status === 'processing') startPolling(jobId);
      toast('Render queued — processing ✓');
      return json;
    } catch (err) {
      console.error('[VideoEditor] render failed:', err);
      setRenderStatus('failed');
      setErrorMessage(err.message);
      toast(`Render failed: ${err.message}`);
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [clientId, clip, projectId, saveProject, buildRenderPayload, startPolling, toast]);

  return {
    // state
    projectId, title, clip, duration, trimStart, trimEnd,
    overlays, activeOverlayId, music, isSaving, isDirty, subtitlesLoading,
    filterPreset, filterCss,
    renderStatus, renderJobId, outputUrl, errorMessage,
    // title
    setTitle: updateTitle,
    // clip / project
    loadClip, loadProject, clearProject, setMeasuredDuration,
    // trim
    updateTrim,
    // overlays
    addOverlay, updateOverlay, removeOverlay, addSubtitles,
    selectOverlay, deselectOverlay, toggleOverlay,
    // music
    selectTrack, updateMusic,
    // filter
    selectFilter,
    // persistence / render
    saveProject, submitRender, buildRenderPayload,
  };
}
